import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import IkiSymbol from '@/components/IkiSymbol';
import '@/styles/Alignment.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Daily inspirational messages
const DAILY_MESSAGES = [
  "Today, you are exactly where you need to be. Trust the process.",
  "Your presence is a gift to the world. Shine your light today.",
  "Every breath you take is an opportunity to begin again.",
  "You are worthy of all the peace, love, and abundance you desire.",
  "What you seek is also seeking you. Stay open to the magic.",
  "Your intuition is your superpower. Listen to its whispers today.",
  "You are not here to be perfect. You are here to be real.",
  "The universe is conspiring in your favor. Trust the timing.",
  "You have everything within you to create the life you dream of.",
  "Today is a new chapter. Write it with intention and grace."
];

function Alignment() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dailyAlignment, setDailyAlignment] = useState(null);
  const [dailyMessage, setDailyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Multi-step flow state
  const [currentStep, setCurrentStep] = useState(1); // 1-5
  const totalSteps = 5;
  
  // Step 1: Breathing
  const [breathingActive, setBreathingActive] = useState(false);
  const [breathPhase, setBreathPhase] = useState('inhale'); // inhale, hold, exhale
  const [breathCount, setBreathCount] = useState(0);
  const [breathingCompleted, setBreathingCompleted] = useState(false);
  
  // Step 2: Message (uses dailyAlignment)
  // Step 3: Voice/Affirmation
  const [showAffirmation, setShowAffirmation] = useState(false);
  const [userVoiceRecording, setUserVoiceRecording] = useState(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const audioRef = useRef(new Audio());
  const breathingIntervalRef = useRef(null);

  // Cleanup breathing interval on unmount
  useEffect(() => {
    return () => {
      if (breathingIntervalRef.current) clearInterval(breathingIntervalRef.current);
    };
  }, []);
  
  // Step 4: Emotional Data
  const [emotionalData, setEmotionalData] = useState({
    mood: 5,
    energy: 5,
    focus: 5,
    stress: 5,
    clarity: 5,
    notes: ''
  });
  const [emotionalDataSaved, setEmotionalDataSaved] = useState(false);
  
  // Step 5: Integration/Finish

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      loadDailyAlignment(parsedUser.user_id);
      loadUserVoiceRecording(parsedUser.user_id);
    } else {
      setLoading(false);
      navigate('/onboarding');
    }
  }, [navigate]);

  const loadDailyAlignment = async (userId) => {
    try {
      const response = await axios.get(`${API}/daily-alignment/today?user_id=${userId}`);
      setDailyAlignment(response.data);
      
      // Check if emotional data already submitted today
      const emotionalResponse = await axios.get(`${API}/emotional-data/today?user_id=${userId}`);
      if (emotionalResponse.data) {
        setEmotionalDataSaved(true);
        // If on step 4 and already completed, skip to step 5
        if (currentStep === 4) {
          setCurrentStep(5);
        }
      }
    } catch (error) {
      console.error('Error loading daily alignment:', error);
      toast.error('Could not load today\'s alignment message');
    } finally {
      // Set daily message (use server message or pick a random one)
      if (dailyAlignment && dailyAlignment.message) {
        setDailyMessage(dailyAlignment.message);
      } else {
        // Pick a random message based on today's date (consistent for the day)
        const today = new Date().toDateString();
        const seed = today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const messageIndex = seed % DAILY_MESSAGES.length;
        setDailyMessage(DAILY_MESSAGES[messageIndex]);
      }
      setLoading(false);
    }
  };

  const loadUserVoiceRecording = async (userId) => {
    try {
      // Get the user's most recent voice recording from Voice Lab
      const response = await axios.get(`${API}/voice-recordings?user_id=${userId}&limit=1`);
      if (response.data && response.data.length > 0) {
        setUserVoiceRecording(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading voice recording:', error);
    }
  };

  const playMyVoice = async () => {
    if (!userVoiceRecording) {
      toast.error("You don't have a voice affirmation yet", {
        action: { label: 'Go to Voice Lab', onClick: () => navigate('/studio') },
        duration: 6000,
      });
      return;
    }

    try {
      if (isPlayingVoice) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlayingVoice(false);
        return;
      }

      // Get the audio file
      const response = await axios.get(
        `${API}/audio/file/${user.user_id}/voice_lab/${userVoiceRecording.filename}`
      );
      
      if (response.data.success && response.data.audio_data) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(response.data.audio_data), c => c.charCodeAt(0))],
          { type: 'audio/webm' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setIsPlayingVoice(false);
          setShowAffirmation(true);
          toast.success("Affirmation complete! ✨");
        };
        await audioRef.current.play();
        setIsPlayingVoice(true);
        toast.success('Playing your voice affirmation');
      }
    } catch (error) {
      console.error('Error playing voice:', error);
      toast.error('Failed to play voice recording');
    }
  };

  // Step Navigation
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const saveEmotionalData = async () => {
    if (!user) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const userCreatedDate = new Date(user.created_at || Date.now());
      const dayIndex = Math.floor((Date.now() - userCreatedDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // Get protocol day from user's microdosing calendar (if exists)
      let protocolDay = null;
      try {
        const calendarResponse = await axios.get(`${API}/microdosing-calendar/${user.user_id}/${today}`);
        if (calendarResponse.data) {
          protocolDay = calendarResponse.data.day_type; // "on" | "off" | "integration"
        }
      } catch (err) {
        // No calendar data yet, that's okay
      }
      
      await axios.post(`${API}/emotional-data`, {
        user_id: user.user_id,
        date: today,
        day_index: dayIndex,
        protocol_day: protocolDay,
        ...emotionalData
      });
      
      setEmotionalDataSaved(true);
      toast.success('Emotional check-in saved!');
      goToNextStep(); // Move to next step after saving
    } catch (error) {
      console.error('Error saving emotional data:', error);
      toast.error('Could not save emotional data');
    }
  };

  const startBreathing = () => {
    setBreathingActive(true);
    setBreathCount(0);
    setBreathPhase('inhale');
    
    // Breathing pattern: 4s inhale, 4s hold, 4s exhale (5 cycles = 60s)
    let phase = 'inhale';
    let count = 0;
    
    breathingIntervalRef.current = setInterval(() => {
      count++;

      if (count % 3 === 1) {
        phase = 'inhale';
        setBreathPhase('inhale');
      } else if (count % 3 === 2) {
        phase = 'hold';
        setBreathPhase('hold');
      } else {
        phase = 'exhale';
        setBreathPhase('exhale');
      }
      
      setBreathCount(Math.floor((count - 1) / 3) + 1); // cycle 1 = counts 1-3, cycle 2 = 4-6, etc.

      // Stop after 5 complete cycles (15 phases)
      if (count >= 15) {
        clearInterval(breathingIntervalRef.current);
        breathingIntervalRef.current = null;
        setBreathingActive(false);
        setBreathingCompleted(true);
        toast.success('Breathing exercise complete! 🌿');
      }
    }, 4000);
  };

  if (loading) {
    return (
      <div className="alignment-container">
        <div className="loading-state">
          <IkiSymbol name="healing" size={48} />
          <p>Loading your alignment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alignment-container">
      {/* Header with Progress */}
      <div className="alignment-header">
        <h1>Daily Alignment</h1>
        <p className="alignment-subtitle">
          Step {currentStep} of {totalSteps}
        </p>
        
        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '4px',
          background: 'rgba(30, 107, 104, 0.1)',
          borderRadius: '2px',
          marginTop: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${(currentStep / totalSteps) * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #1E6B68 0%, #2D8B87 100%)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* STEP 1: Breathing Exercise */}
      {currentStep === 1 && (
        <div className="step-content">
          <div className="breathing-section">
            <div className="breathing-card">
              <h2>Breathing Exercise</h2>
              <p className="breathing-instruction">Take one minute to center yourself</p>
              
              {!breathingActive && !breathingCompleted ? (
                <button onClick={startBreathing} className="btn-start-breathing">
                  <IkiSymbol name="healing" size={32} />
                  <span>Start Breathing (1 min)</span>
                </button>
              ) : breathingActive ? (
                <div className="breathing-animation">
                  <div className={`breath-circle breath-${breathPhase}`}>
                    <span className="breath-text">{breathPhase}</span>
                  </div>
                  <p className="breath-count">Cycle {breathCount} of 5</p>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '32px',
                  background: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '16px',
                  marginTop: '24px'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✓</div>
                  <p style={{ fontSize: '1.1rem', color: '#4CAF50', fontWeight: '500' }}>
                    Breathing complete
                  </p>
                </div>
              )}
              
              {breathingCompleted && (
                <button 
                  onClick={goToNextStep} 
                  className="btn-next-step"
                  style={{
                    marginTop: '24px',
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '24px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Next: Your Message
                </button>
              )}
              
              {!breathingActive && !breathingCompleted && (
                <button 
                  onClick={() => {
                    setBreathingCompleted(true);
                  }}
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    padding: '12px',
                    background: 'transparent',
                    color: '#666',
                    border: 'none',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Skip to message
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: Your Message for Today */}
      {currentStep === 2 && (
        <div className="step-content">
        <div className="alignment-message-section">
          <div className="message-card">
            <div className="message-icon">
              <IkiSymbol name="sparkles" size={48} />
            </div>
            <h2>Your Message for Today</h2>
            <p className="daily-message">{dailyMessage || "You are exactly where you need to be. Trust the journey."}</p>
            <span className="message-date">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button 
              onClick={goToPreviousStep} 
              style={{
                flex: 1,
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                color: '#1E6B68',
                border: '2px solid #1E6B68',
                borderRadius: '24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ← Previous
            </button>
            <button 
              onClick={goToNextStep} 
              className="btn-next-step"
              style={{
                flex: 2,
                padding: '16px',
                background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '24px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Next: Voice Affirmation →
            </button>
          </div>
        </div>
        </div>
      )}

      {/* STEP 3: Voice/Affirmation */}
      {currentStep === 3 && (
        <div className="step-content">
          <div className="affirmation-section" style={{ textAlign: 'center', padding: '32px' }}>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.75rem',
              color: '#1E6B68',
              marginBottom: '16px'
            }}>
              Voice Affirmation
            </h2>
            <p style={{ color: '#666', marginBottom: '32px', lineHeight: '1.7' }}>
              Listen to today's affirmation in your own voice or our guiding voice
            </p>
            
            {showAffirmation && (
              <div style={{
                padding: '16px',
                background: 'rgba(168, 230, 206, 0.2)',
                border: '2px solid rgba(168, 230, 206, 0.5)',
                borderRadius: '12px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <p style={{ color: '#1E6B68', fontWeight: '600', margin: 0 }}>
                  ✨ Today's Affirmation Complete ✨
                </p>
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '16px',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <button 
                onClick={playMyVoice}
                className="btn-affirmation" 
                style={{
                  padding: '16px 24px',
                  background: isPlayingVoice 
                    ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)'
                    : 'rgba(30, 107, 104, 0.1)',
                  color: isPlayingVoice ? '#fff' : '#1E6B68',
                  border: '2px solid #1E6B68',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontSize: '1rem',
                  fontWeight: isPlayingVoice ? '600' : '400'
                }}
              >
                <IkiSymbol name={isPlayingVoice ? "stop" : "mic"} size={24} />
                <span>{isPlayingVoice ? "Stop" : userVoiceRecording ? "Play My Voice" : "Record Voice First"}</span>
              </button>
              <button className="btn-affirmation" style={{
                padding: '16px 24px',
                background: 'rgba(30, 107, 104, 0.1)',
                border: '2px solid #1E6B68',
                borderRadius: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                fontSize: '1rem'
              }}>
                <IkiSymbol name="play" size={24} />
                <span>Play Guiding Voice</span>
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '48px', width: '100%', maxWidth: '600px' }}>
              <button 
                onClick={goToPreviousStep} 
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#1E6B68',
                  border: '2px solid #1E6B68',
                  borderRadius: '24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ← Previous
              </button>
              <button 
                onClick={goToNextStep} 
                className="btn-next-step"
                style={{
                  flex: 2,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Next: Emotional Check-In →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip Breathing Option */}
      {!showAffirmation && (
        <button 
          className="btn-skip"
          onClick={() => setShowAffirmation(true)}
        >
          Skip to Message
        </button>
      )}

      {/* STEP 4: Emotional Check-In */}
      {currentStep === 4 && !emotionalDataSaved && (
        <div className="step-content">
        <div style={{
          marginTop: '40px',
          padding: '24px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(30, 107, 104, 0.1)'
        }}>
          <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.5rem',
            color: '#1E6B68',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            How are you feeling today?
          </h3>
          <p style={{
            textAlign: 'center',
            color: '#666',
            marginBottom: '24px',
            fontSize: '0.95rem'
          }}>
            Take a moment to check in with yourself. Rate each dimension from 0 (low) to 10 (high).
          </p>

          {[
            { key: 'mood', label: 'Mood', icon: 'heart' },
            { key: 'energy', label: 'Energy', icon: 'energy' },
            { key: 'focus', label: 'Focus', icon: 'focus' },
            { key: 'stress', label: 'Stress', icon: 'healing' },
            { key: 'clarity', label: 'Clarity', icon: 'vision' }
          ].map((dimension) => (
            <div key={dimension.key} style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IkiSymbol name={dimension.icon} size={20} />
                  <label style={{
                    fontWeight: '500',
                    color: '#1E6B68',
                    fontSize: '1rem'
                  }}>
                    {dimension.label}
                  </label>
                </div>
                <span style={{
                  fontWeight: '600',
                  color: '#2D8B87',
                  fontSize: '1.1rem'
                }}>
                  {emotionalData[dimension.key]}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="10"
                value={emotionalData[dimension.key]}
                onChange={(e) => setEmotionalData({
                  ...emotionalData,
                  [dimension.key]: parseInt(e.target.value)
                })}
                style={{
                  width: '100%',
                  accentColor: '#2D8B87'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
                color: '#999',
                marginTop: '4px'
              }}>
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
          ))}

          <div style={{ marginTop: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '500',
              color: '#1E6B68',
              marginBottom: '8px'
            }}>
              Notes (optional)
            </label>
            <textarea
              value={emotionalData.notes}
              onChange={(e) => setEmotionalData({ ...emotionalData, notes: e.target.value })}
              placeholder="Any thoughts or observations..."
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #B8D3CF',
                fontSize: '0.95rem',
                fontFamily: 'Manrope, sans-serif',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <button 
              onClick={goToPreviousStep} 
              style={{
                flex: 1,
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                color: '#1E6B68',
                border: '2px solid #1E6B68',
                borderRadius: '24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ← Previous
            </button>
            <button
              onClick={saveEmotionalData}
              style={{
                flex: 2,
                padding: '16px',
                background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '24px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(30, 107, 104, 0.3)',
                transition: 'transform 0.2s ease'
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Next: Complete Alignment →
            </button>
          </div>
        </div>
        </div>
      )}

      {/* STEP 5: Integration & Completion */}
      {currentStep === 5 && (
        <div className="step-content">
          <div style={{
            textAlign: 'center',
            padding: '48px 24px'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '24px' }}>✨</div>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '2rem',
              color: '#1E6B68',
              marginBottom: '16px'
            }}>
              Alignment Complete
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              lineHeight: '1.7',
              marginBottom: '32px',
              maxWidth: '500px',
              margin: '0 auto 32px'
            }}>
              You've completed today's alignment practice. Take this energy with you throughout your day.
            </p>
            
            {/* Integration Actions */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: '400px',
              margin: '0 auto 40px'
            }}>
              <button 
                onClick={() => navigate('/mind-mirror')}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
              >
                <IkiSymbol name="edit" size={20} />
                Journal This Experience
              </button>
              
              <button 
                onClick={() => navigate('/')}
                style={{
                  padding: '16px 24px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#1E6B68',
                  border: '2px solid #1E6B68',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
              >
                <IkiSymbol name="home" size={20} />
                Return Home
              </button>
            </div>
            
            <div style={{
              padding: '20px',
              background: 'rgba(168, 230, 206, 0.2)',
              borderRadius: '16px',
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              <p style={{
                fontSize: '0.95rem',
                color: '#1E6B68',
                fontStyle: 'italic',
                lineHeight: '1.6'
              }}>
                "The practice is the path. Each day you show up, you're transforming."
              </p>
            </div>
          </div>
        </div>
      )}

      {emotionalDataSaved && currentStep !== 5 && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'rgba(76, 175, 80, 0.1)',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#4CAF50'
        }}>
          ✓ Today's emotional check-in complete
        </div>
      )}
    </div>
  );
}

export default Alignment;
